/**
 * Marketing-site Routes (brand-aware)
 *
 * Only includes routes that are actually linked from the site.
 * Template/demo pages from Material Kit have been removed to reduce bundle size.
 * All route components are lazy-loaded to keep the main bundle small.
 *
 * The /affiliates page is brand-forked — each build only imports its own
 * brand's page chunk.
 */

import { lazy, Suspense } from "react";

const BRAND = process.env.REACT_APP_BRAND;
if (BRAND !== "ringfoods" && BRAND !== "ringoperator") {
  throw new Error(
    `routes.js: unknown or missing REACT_APP_BRAND: '${BRAND}'. Must be 'ringfoods' or 'ringoperator'.`
  );
}

const AffiliateProgram =
  BRAND === "ringoperator"
    ? lazy(() => import("pages/RingOperatorAffiliateProgram"))
    : lazy(() => import("pages/AffiliateProgram"));

const Privacy = lazy(() => import("layouts/pages/support/privacy"));
const Terms = lazy(() => import("layouts/pages/support/terms"));
const SmsPolicy = lazy(() => import("pages/Support/SmsPolicy"));

// Keyword landing pages — one per Google Ads ad group, RingFoods only.
// This list is duplicated in scripts/landing-routes.js (which prerender.js and
// generate-sitemap.js both read) and in the CloudFront function's STATIC_EXACT
// map in manage.sh. CRA forbids importing from outside src/, so this copy has
// to be maintained by hand. Miss the CloudFront entry and the URL is served the
// homepage's prerendered HTML (unknown single-segment paths fall through to the
// business-slug branch) — a 200 with the wrong canonical.
const LANDING_PAGES =
  BRAND === "ringfoods"
    ? [
        {
          name: "ai-voice-agent-for-restaurants",
          route: "/ai-voice-agent-for-restaurants",
          Component: lazy(() => import("pages/RingFoodsLanding/AiVoiceAgentForRestaurants")),
        },
        {
          name: "restaurant-phone-answering",
          route: "/restaurant-phone-answering",
          Component: lazy(() => import("pages/RingFoodsLanding/RestaurantPhoneAnswering")),
        },
        {
          name: "ai-restaurant-reservations",
          route: "/ai-restaurant-reservations",
          Component: lazy(() => import("pages/RingFoodsLanding/AiRestaurantReservations")),
        },
      ]
    : [];

const routes = [
  {
    name: "affiliates",
    route: "/affiliates",
    component: (
      <Suspense fallback={null}>
        <AffiliateProgram />
      </Suspense>
    ),
  },
  ...LANDING_PAGES.map(({ name, route, Component }) => ({
    name,
    route,
    component: (
      <Suspense fallback={null}>
        <Component />
      </Suspense>
    ),
  })),
  {
    name: "privacy",
    route: "/pages/support/privacy",
    component: (
      <Suspense fallback={null}>
        <Privacy />
      </Suspense>
    ),
  },
  {
    name: "terms",
    route: "/pages/support/terms",
    component: (
      <Suspense fallback={null}>
        <Terms />
      </Suspense>
    ),
  },
  {
    name: "sms-policy",
    route: "/sms-policy",
    component: (
      <Suspense fallback={null}>
        <SmsPolicy />
      </Suspense>
    ),
  },
];

export default routes;
